import { PayloadAction } from '@reduxjs/toolkit';
import createAppSlice from 'app/createAppSlice';
import type {
  SessionSliceType,
  TourAttributeUpdate,
  refreshTokenUpdateProps,
} from './types';

const initialState: SessionSliceType = {
  agentId: null,
  entitlements: {},
  features: [],
  token: null,
  accessToken: '',
  refreshToken: '',
  userLoggedIn: false,
  missingPii: [],
  /* `null` = not yet known. Distinct from a stamped timestamp, and the tour's
     gate treats both null and '' as "no previous visit". */
  lastVisitedAt: null,
  /* Defaults, not "unknown": a response without `userAttributes` (masquerade,
     or a backend yet to ship the field) must read as a user who has never
     skipped and never opted out, so the tour still shows. */
  userAttributes: { skipTourCount: 0, dontShowTour: false },
  userOffline: false,
  sessionId: '',
  currentReport: null,
  migrateReport: false,
};

const sessionSlice = createAppSlice({
  name: 'session',
  initialState,
  reducers: {
    setSessionSlice(state, action: PayloadAction<SessionSliceType>) {
      return {
        ...state,
        ...action.payload,
      };
    },
    refreshTokenUpdate(state, action: PayloadAction<refreshTokenUpdateProps>) {
      const { accessToken, refreshToken } = action.payload;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
    },
    setMissingPii(state, action: PayloadAction<string[]>) {
      state.missingPii = action.payload;
    },
    /* Targeted rather than reusing `setSessionSlice`, which demands the whole
       `SessionSliceType`. Dispatched only AFTER the POST resolves, so the store
       never claims a change the server did not record.

       MERGES rather than replaces: the request sends only the field the user
       changed, so replacing would drop the other one and the gate would read a
       stale default. The literal defaults are the base in case the slice was
       rehydrated from before this field existed. */
    setTourAttributes(state, action: PayloadAction<TourAttributeUpdate>) {
      state.userAttributes = {
        skipTourCount: 0,
        dontShowTour: false,
        ...state.userAttributes,
        ...action.payload,
      };
    },
    addTuidVerified(state) {
      state.entitlements['itps:verified'] = ['tuid'];
    },
    addIdauthVerified(state) {
      state.entitlements['itps:verified'] = ['idauth'];
    },
    setCurrentReport(state, action: PayloadAction<string | null>) {
      state.currentReport = action.payload;
    },
    setPlaidOauthState(
      state,
      action: PayloadAction<{ linkToken: string; itemId: string | null }>,
    ) {
      state.plaidOauthLinkToken = action.payload.linkToken;
      state.plaidOauthItemId = action.payload.itemId;
    },
    clearPlaidOauthState(state) {
      state.plaidOauthLinkToken = null;
      state.plaidOauthItemId = null;
    },
    // Angular parity — the `USER_INFO_RETRIEVED` action dispatched
    // from `session.service.ts:422-427` after a successful token
    // validation. Stashes the fields the /sign-in/verify-user page
    // needs to render the identity form.
    setUnverifiedUserInfo(
      state,
      action: PayloadAction<{
        emailAddress: string | null;
        validationFields: string[];
        locale: string | null;
      }>,
    ) {
      state.unverifiedEmailAddress = action.payload.emailAddress;
      state.unverifiedValidationFields = action.payload.validationFields;
      state.unverifiedLocale = action.payload.locale;
    },
    clearUnverifiedUserInfo(state) {
      state.unverifiedEmailAddress = null;
      state.unverifiedValidationFields = [];
      state.unverifiedLocale = null;
    },
    userSignOut() {
      return initialState;
    },
  },
});

export const {
  setSessionSlice,
  userSignOut,
  refreshTokenUpdate,
  setMissingPii,
  setTourAttributes,
  addTuidVerified,
  addIdauthVerified,
  setCurrentReport,
  setPlaidOauthState,
  clearPlaidOauthState,
  setUnverifiedUserInfo,
  clearUnverifiedUserInfo,
} = sessionSlice.actions;
export default sessionSlice.reducer;
